import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import {
  useFeedbackPeriods, useMyEvaluatorsApi, useEmployeeNames,
  countActiveNominations, NOMINATION_ROLES, EXTERNAL_NOMINATION_ROLES,
  MIN_TOTAL_NOMINATIONS, MAX_TOTAL_NOMINATIONS, employeeAsync,
  isValidEmail, externalDisplayName,
} from './config';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';

// Task 28 — PRD PM §4.2: çalışan kendi 360° değerlendiricilerini önerir
// (min 3 / max 10 toplam, rejected hariç — bkz. backend
// EvaluatorNominationDataAccess.ApplyNominationsAsync). Sayaç YALNIZ görsel bir
// rehber — sunucu tarafı doğrulama (minThree/maxTen/duplicate/selfNotAllowed)
// gerçek kaynak, hatalar standart Axios interceptor ile gösterilir (bileşen
// içinde toast YOK).
const Container = styled.div``;

const PageHeader = styled.div`
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PageHint = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6c757d;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const PeriodSelectWrap = styled.div`
  max-width: 360px;
  margin-bottom: 18px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: 12px;
  align-items: end;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #f5f7fa;
  margin-bottom: 6px;
  font-size: 13px;
`;

const RoleTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #eff6ff;
  color: #0291ef;
`;

// P1 — harici (şirket dışı) değerlendirici rozeti.
const ExternalTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f3e8ff;
  color: #7c3aed;
`;

const StatusTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${(p) => (p.$status === 'approved' ? '#DCFCE7' : p.$status === 'rejected' ? '#F8D7DA' : '#FFFBEB')};
  color: ${(p) => (p.$status === 'approved' ? '#16A34A' : p.$status === 'rejected' ? '#B02A37' : '#D97706')};
`;

const RemoveBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #6c757d;
  display: flex;
  align-items: center;
  &:hover { color: #e53935; }
`;

const CounterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$ok ? '#16A34A' : '#D97706')};
`;

const HintText = styled.div`
  font-size: 12px;
  color: #b02a37;
  margin-top: 6px;
`;

const EmptyBox = styled.div`
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

// Taslak liste anahtarı: iç satırda çalışan id'si, harici satırda e-posta
// (öneride harici kimlik yalnız e-postadır — backend ile aynı tanım).
const stagedItemKey = (s) => (s.isExternal ? `ext:${(s.externalEmail || '').toLowerCase()}` : s.evaluatorEmployeeId);

export default function MyEvaluatorsPage() {
  const { t } = useTranslation();

  const { periods, periodsFetching } = useFeedbackPeriods();
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) setSelectedPeriodId(periods[0].id);
  }, [periods, selectedPeriodId]);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  const { nominations, fetching, refresh, propose } = useMyEvaluatorsApi(selectedPeriodId);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedRole, setSelectedRole] = useState('peer');
  const [staged, setStaged] = useState([]);
  const [duplicateHint, setDuplicateHint] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // P1 — harici (şirket dışı) değerlendirici alanları (ad+e-posta+rol; rol
  // yalnız peer|senior — bkz. backend externalRoleInvalid).
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalRole, setExternalRole] = useState('peer');
  const [externalDuplicateHint, setExternalDuplicateHint] = useState(false);

  // Dönem değişince taslak liste sıfırlanır.
  useEffect(() => {
    setStaged([]); setDuplicateHint(false);
    setExternalName(''); setExternalEmail(''); setExternalDuplicateHint(false);
  }, [selectedPeriodId]);

  const existingActiveIds = useMemo(
    () => new Set(nominations.filter((n) => n.status !== 'rejected' && !n.isExternal).map((n) => n.evaluatorEmployeeId)),
    [nominations],
  );

  // Mükerrer harici kontrolü e-posta ÜZERİNDEN (büyük/küçük harf duyarsız) —
  // backend ApplyNominationsAsync ile aynı tanım.
  const existingActiveExternalEmails = useMemo(
    () => new Set(nominations
      .filter((n) => n.status !== 'rejected' && n.isExternal)
      .map((n) => (n.externalEmail || '').trim().toLowerCase())),
    [nominations],
  );

  // İsim çözümü yalnız İÇ değerlendiriciler için — harici satırın adı/e-postası
  // kendi alanlarından gelir (evaluatorEmployeeId öneride boş Guid'dir).
  const nameIds = useMemo(() => [
    ...nominations.filter((n) => !n.isExternal).map((n) => n.evaluatorEmployeeId),
    ...staged.filter((s) => !s.isExternal).map((s) => s.evaluatorEmployeeId),
  ], [nominations, staged]);
  const employeeNames = useEmployeeNames(nameIds);

  const roleOptions = useMemo(() => NOMINATION_ROLES.map((r) => ({
    id: r, name: t(`performance.feedback360.role.${r}`),
  })), [t]);
  const externalRoleOptions = useMemo(() => EXTERNAL_NOMINATION_ROLES.map((r) => ({
    id: r, name: t(`performance.feedback360.role.${r}`),
  })), [t]);

  const handleAddStaged = useCallback(() => {
    if (!selectedEmployeeId) return;
    const isDuplicate = existingActiveIds.has(selectedEmployeeId)
      || staged.some((s) => !s.isExternal && s.evaluatorEmployeeId === selectedEmployeeId);
    if (isDuplicate) { setDuplicateHint(true); return; }
    setDuplicateHint(false);
    setStaged((prev) => [...prev, { evaluatorEmployeeId: selectedEmployeeId, evaluatorRole: selectedRole }]);
    setSelectedEmployeeId(null);
  }, [selectedEmployeeId, selectedRole, existingActiveIds, staged]);

  const handleAddExternalStaged = useCallback(() => {
    const email = externalEmail.trim();
    if (!isValidEmail(email)) return;
    const emailKey = email.toLowerCase();
    const isDuplicate = existingActiveExternalEmails.has(emailKey)
      || staged.some((s) => s.isExternal && (s.externalEmail || '').trim().toLowerCase() === emailKey);
    if (isDuplicate) { setExternalDuplicateHint(true); return; }
    setExternalDuplicateHint(false);
    setStaged((prev) => [...prev, {
      isExternal: true,
      externalEmail: email,
      externalFullName: externalName.trim(),
      evaluatorRole: externalRole,
    }]);
    setExternalName('');
    setExternalEmail('');
  }, [externalEmail, externalName, externalRole, existingActiveExternalEmails, staged]);

  const handleRemoveStaged = useCallback((stagedKey) => {
    setStaged((prev) => prev.filter((s) => stagedItemKey(s) !== stagedKey));
  }, []);

  const activeCount = countActiveNominations(nominations);
  const totalCount = activeCount + staged.length;
  const counterOk = totalCount >= MIN_TOTAL_NOMINATIONS && totalCount <= MAX_TOTAL_NOMINATIONS;
  const atMax = totalCount >= MAX_TOTAL_NOMINATIONS;

  const handleSubmit = useCallback(async () => {
    if (!selectedPeriodId || staged.length === 0) return;
    setSubmitting(true);
    const ok = await propose(selectedPeriodId, staged.map((s) => (s.isExternal
      ? {
        isExternal: true,
        externalEmail: s.externalEmail,
        externalFullName: s.externalFullName,
        evaluatorRole: s.evaluatorRole,
      }
      : {
        evaluatorEmployeeId: s.evaluatorEmployeeId,
        evaluatorRole: s.evaluatorRole,
      })));
    setSubmitting(false);
    if (ok) {
      setStaged([]);
      refresh();
    }
  }, [selectedPeriodId, staged, propose, refresh]);

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('performance.feedback360.myEvaluators.title')}</PageTitle>
        <PageHint>{t('performance.feedback360.myEvaluators.hint')}</PageHint>
      </PageHeader>

      <PeriodSelectWrap>
        <GenericSelect
          label={t('performance.goal.selectPeriod')}
          value={selectedPeriodId}
          onChange={setSelectedPeriodId}
          options={periodOptions}
          idKey="id"
          labelKey="name"
        />
      </PeriodSelectWrap>

      {!periodsFetching && periods.length === 0 && (
        <Card><EmptyBox>{t('performance.goal.noPeriods')}</EmptyBox></Card>
      )}

      {selectedPeriodId && (
        <>
          <Card>
            <SectionTitle>{t('performance.feedback360.myEvaluators.existing')}</SectionTitle>
            {!fetching && nominations.length === 0 && (
              <EmptyBox>{t('performance.feedback360.myEvaluators.noneYet')}</EmptyBox>
            )}
            {nominations.map((n) => (
              <ListItem key={n.id}>
                <span>{n.isExternal ? externalDisplayName(n) : (employeeNames[n.evaluatorEmployeeId] || '...')}</span>
                <span style={{ display: 'flex', gap: 8 }}>
                  {n.isExternal && <ExternalTag>{t('performance.feedback360.external')}</ExternalTag>}
                  <RoleTag>{t(`performance.feedback360.role.${n.evaluatorRole}`, { defaultValue: n.evaluatorRole })}</RoleTag>
                  <StatusTag $status={n.status}>
                    {t(`performance.feedback360.nominationStatus.${n.status}`, { defaultValue: n.status })}
                  </StatusTag>
                </span>
              </ListItem>
            ))}
          </Card>

          <Card>
            <SectionTitle>{t('performance.feedback360.myEvaluators.addNew')}</SectionTitle>
            <Row>
              <AsyncGenericSelect
                label={t('performance.feedback360.myEvaluators.searchEmployee')}
                value={selectedEmployeeId}
                onChange={setSelectedEmployeeId}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
                disabled={atMax}
              />
              <GenericSelect
                label={t('performance.feedback360.myEvaluators.role')}
                value={selectedRole}
                onChange={setSelectedRole}
                options={roleOptions}
              />
              <Button variant="outline" onClick={handleAddStaged} disabled={!selectedEmployeeId || atMax}>
                {t('performance.feedback360.myEvaluators.addToList')}
              </Button>
            </Row>
            {duplicateHint && <HintText>{t('performance.feedback360.myEvaluators.duplicateHint')}</HintText>}

            <SectionTitle style={{ marginTop: 20 }}>
              {t('performance.feedback360.myEvaluators.addExternalTitle')}
            </SectionTitle>
            <PageHint style={{ marginBottom: 10 }}>{t('performance.feedback360.myEvaluators.externalHint')}</PageHint>
            <Row>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <TextInput
                  label={t('performance.feedback360.externalFullName')}
                  value={externalName}
                  onChange={(e) => setExternalName(e.target.value)}
                  disabled={atMax}
                />
                <TextInput
                  label={t('performance.feedback360.externalEmail')}
                  type="email"
                  value={externalEmail}
                  onChange={(e) => setExternalEmail(e.target.value)}
                  disabled={atMax}
                />
              </div>
              <GenericSelect
                label={t('performance.feedback360.myEvaluators.role')}
                value={externalRole}
                onChange={setExternalRole}
                options={externalRoleOptions}
              />
              <Button
                variant="outline"
                onClick={handleAddExternalStaged}
                disabled={!isValidEmail(externalEmail) || atMax}
              >
                {t('performance.feedback360.myEvaluators.addToList')}
              </Button>
            </Row>
            {externalDuplicateHint && <HintText>{t('performance.feedback360.myEvaluators.duplicateHint')}</HintText>}

            {staged.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {staged.map((s) => (
                  <ListItem key={stagedItemKey(s)}>
                    <span>{s.isExternal ? externalDisplayName(s) : (employeeNames[s.evaluatorEmployeeId] || '...')}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {s.isExternal && <ExternalTag>{t('performance.feedback360.external')}</ExternalTag>}
                      <RoleTag>{t(`performance.feedback360.role.${s.evaluatorRole}`)}</RoleTag>
                      <RemoveBtn onClick={() => handleRemoveStaged(stagedItemKey(s))} type="button">
                        <X size={14} />
                      </RemoveBtn>
                    </span>
                  </ListItem>
                ))}
              </div>
            )}

            <CounterBar $ok={counterOk}>
              {t('performance.feedback360.myEvaluators.counter', { count: totalCount, min: MIN_TOTAL_NOMINATIONS, max: MAX_TOTAL_NOMINATIONS })}
            </CounterBar>

            <Actions>
              <Button onClick={handleSubmit} disabled={staged.length === 0 || submitting} loading={submitting}>
                {t('performance.feedback360.myEvaluators.submitBatch')}
              </Button>
            </Actions>
          </Card>
        </>
      )}
    </Container>
  );
}
